<template>
  <v-container fluid class="cl-container flex-grow-1">

    <!-- Loading -->
    <v-row
      v-if="loading"
      align="center"
      justify="center"
      style="height: 100%;"
      no-gutters
    >
      <div class="cl-loading">
        <v-progress-circular :size="44" :width="4" color="#2D4B95" indeterminate />
        <p class="cl-loading-text mb-0">Cargando empresas…</p>
      </div>
    </v-row>

    <!-- Grid de empresas -->
    <v-row v-else>
      <v-col cols="12" class="py-1">
        <div class="cl-search-box" :class="{ 'cl-search-box--active': searchQuery }">
          <v-icon size="15" color="#8097C4">mdi-magnify</v-icon>
          <input
            v-model="searchQuery"
            class="cl-search-input"
            placeholder="Buscar por nombre o RUT…"
          />
          <v-icon
            v-if="searchQuery"
            size="15"
            color="#AABBD8"
            style="cursor:pointer"
            @click="searchQuery = ''"
          >mdi-close</v-icon>
        </div>
      </v-col>

      <v-col
        v-for="(item, index) in filteredCompanies"
        :key="item.company.id"
        cols="12" sm="6" md="4" lg="3" xl="2"
        class="pa-2"
      >
        <div
          class="cl-card"
          :class="{ 'cl-card--selected': isSelected(item) }"
          :style="{ animationDelay: `${index * 40}ms` }"
          @click="selectCompany(item)"
        >

          <!-- Franja superior con logo -->
          <div class="cl-card-top">
            <v-chip
              x-small
              class="cl-status"
              :class="item.status === 'active' ? 'cl-status--active' : 'cl-status--inactive'"
            >
              {{ item.status === 'active' ? 'Activa' : 'Inactiva' }}
            </v-chip>
            <div class="cl-logo-wrap">
              <img
                v-if="item.company.logo"
                :src="item.company.logo"
                class="cl-logo"
                alt=""
              />
              <div v-else class="cl-logo-fallback">
                <v-icon color="white" size="26">mdi-domain</v-icon>
              </div>
            </div>
          </div>

          <!-- Cuerpo -->
          <div class="cl-card-body">
            <p class="cl-name">{{ item.company.name }}</p>
            <span class="cl-role">{{ roleLabel(item.role) }}</span>

            <div class="cl-info-list mt-3">
              <div class="cl-info-row">
                <v-icon size="13" color="#8097C4">mdi-card-account-details-outline</v-icon>
                <span class="cl-info-text">RUT {{ item.company.rut }}</span>
              </div>
              <div v-if="item.company.url" class="cl-info-row">
                <v-icon size="13" color="#8097C4">mdi-web</v-icon>
                <span class="cl-info-text">{{ item.company.url }}</span>
              </div>
              <div v-if="item.company.description" class="cl-info-row cl-info-row--top">
                <v-icon size="13" color="#8097C4">mdi-text-box-outline</v-icon>
                <span class="cl-info-text cl-info-text--wrap">{{ item.company.description }}</span>
              </div>
            </div>

            <!-- Acciones -->
            <div v-if="canEdit(item) || canArchive(item)" class="cl-actions">
              <div class="cl-actions-row">
                <v-btn
                  v-if="canEdit(item)"
                  depressed rounded x-small dark
                  color="#E6A23D"
                  class="cl-btn"
                  @click.stop="openEdit(item)"
                >
                  <v-icon size="13" class="mr-1">mdi-pencil-outline</v-icon>
                  Editar
                </v-btn>
                <v-btn
                  v-if="canArchive(item)"
                  depressed rounded x-small dark
                  color="#DD5050"
                  class="cl-btn"
                  @click.stop="archiveCompanyItem(item)"
                >
                  <v-icon size="13" class="mr-1">mdi-archive-outline</v-icon>
                  Archivar
                </v-btn>
              </div>
            </div>
          </div>

        </div>
      </v-col>

      <!-- Estado vacío -->
      <v-col v-if="!filteredCompanies.length" cols="12">
        <div class="cl-empty">
          <v-icon size="36" color="#B7C0D0">mdi-domain-off</v-icon>
          <div class="cl-empty-title">
            {{ companies.length ? 'No hay resultados para tu búsqueda' : 'No hay empresas para mostrar' }}
          </div>
          <div class="cl-empty-text">
            {{ companies.length ? 'Prueba con otro nombre o RUT.' : 'Crea una empresa para comenzar a trabajar.' }}
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Modal edición -->
    <v-dialog v-model="editDialog" max-width="560" content-class="edit-company-dialog" persistent>
      <v-card class="company-modal" elevation="0">

        <div class="modal-header">
          <div class="header-left">
            <div class="header-icon-wrap">
              <v-icon color="white" size="20">mdi-domain</v-icon>
            </div>
            <div>
              <div class="modal-title">Editar empresa</div>
              <div class="modal-subtitle">Actualiza los datos de la empresa</div>
            </div>
          </div>
          <v-btn icon small @click="editDialog = false" class="close-btn">
            <v-icon color="rgba(255,255,255,0.7)" size="18">mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="modal-body">
          <v-form ref="editForm" v-model="editValid" class="form-wrapper">
            <div class="scroll-area">
              <div class="field-grid">
                <div class="field-group span-2">
                  <label class="field-label">Nombre</label>
                  <v-text-field
                    v-model="editForm.name"
                    placeholder="Ingrese nombre de la empresa"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="requiredRule"
                    class="custom-field"
                  />
                </div>
                <div class="field-group">
                  <label class="field-label">RUT</label>
                  <v-text-field
                    v-model="editForm.rut"
                    placeholder="Ingrese RUT de la empresa"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="requiredRule"
                    class="custom-field"
                  />
                </div>
                <div class="field-group">
                  <label class="field-label">Url sitio web</label>
                  <v-text-field
                    v-model="editForm.url"
                    placeholder="Ingrese sitio web de la empresa"
                    outlined
                    dense
                    hide-details="auto"
                    class="custom-field"
                  />
                </div>
                <div class="field-group span-2">
                  <label class="field-label">Descripción</label>
                  <v-textarea
                    v-model="editForm.description"
                    placeholder="Ingrese descripción de la empresa"
                    outlined
                    dense
                    rows="3"
                    auto-grow
                    hide-details="auto"
                    :rules="requiredRule"
                    class="custom-field"
                  />
                </div>
              </div>
            </div>
          </v-form>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" @click="editDialog = false">Cancelar</button>
          <button class="btn-save" :disabled="saving" @click="saveEdit">
            <v-progress-circular v-if="saving" indeterminate size="15" width="2" color="white" class="mr-1"></v-progress-circular>
            <v-icon v-else size="15" color="white" class="mr-1">mdi-check</v-icon>
            Guardar
          </button>
        </div>

      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import {getCompanies, updateCompany, archiveCompany} from '@/helpers/api/company'

export default {
  name: 'CompaniesIndex',
  data: () => ({
    companies: [],
    selectedItem: null,
    searchQuery: '',
    loading: false,
    saving: false,
    editDialog: false,
    editValid: true,
    editForm: {
      name: null,
      rut: null,
      description: null,
      url: null,
    },
    requiredRule: [value => !!value || 'Campo requerido'],
  }),
  computed: {
    filteredCompanies() {
      const q = (this.searchQuery || '').toString().trim().toLowerCase()
      if (!q) return this.companies
      return this.companies.filter(item => {
        const name = (item.company.name || '').toString().toLowerCase()
        const rut = (item.company.rut || '').toString().toLowerCase()
        return name.includes(q) || rut.includes(q)
      })
    },
  },
  created() {
    this.companyCreatedHandler = () => this.loadCompanies()
    this.$root.$on('company-created', this.companyCreatedHandler)
  },
  mounted() {
    this.loadCompanies()
  },
  beforeDestroy() {
    this.$root.$off('company-created', this.companyCreatedHandler)
  },
  methods: {
    async loadCompanies() {
      this.loading = true
      try {
        const response = await getCompanies()
        this.companies = response.companies || []
        if (this.selectedItem) {
          this.selectedItem = this.companies.find(item => item.company.id === this.selectedItem.company.id) || null
        }
        if (!this.selectedItem && this.companies.length) {
          this.selectCompany(this.companies[0])
        }
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.loading = false
      }
    },
    isSelected(item) {
      return !!this.selectedItem && this.selectedItem.company.id === item.company.id
    },
    selectCompany(item) {
      this.selectedItem = item
      const membership = {
        ...item.membership,
        company: item.company,
        capabilities: item.capabilities,
      }
      this.$store.userStore.commit('setSelectedCompany', membership)
      this.$store.userStore.commit('setSelectedCompanyMembership', membership)
      this.$store.userStore.commit('setCompanyPermissions', item.capabilities)
    },
    openEdit(item) {
      if (item) this.selectCompany(item)
      this.editForm = {
        name: this.selectedItem.company.name,
        rut: this.selectedItem.company.rut,
        description: this.selectedItem.company.description,
        url: this.selectedItem.company.url,
      }
      this.editDialog = true
    },
    async saveEdit() {
      if (!this.$refs.editForm.validate()) return
      this.saving = true
      try {
        const response = await updateCompany(this.selectedItem.company.id, this.editForm)
        if (response.code === 200) {
          this.companies = this.companies.map(item => item.company.id === response.company.company.id ? response.company : item)
          this.selectedItem = response.company
          this.selectCompany(response.company)
          this.editDialog = false
        }
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.saving = false
      }
    },
    async archiveCompanyItem(item) {
      if (item) this.selectCompany(item)
      await this.archiveSelected()
    },
    async archiveSelected() {
      if (!this.selectedItem || !window.confirm('¿Archivar esta empresa?')) return
      try {
        await archiveCompany(this.selectedItem.company.id)
        this.companies = this.companies.filter(item => item.company.id !== this.selectedItem.company.id)
        this.selectedItem = this.companies[0] || null
        if (this.selectedItem) this.selectCompany(this.selectedItem)
      } catch (error) {
        this.notifyError(error)
      }
    },
    canEdit(item) {
      return item.role === 'owner' || item.role === 'company_admin' || !!item.capabilities.edit_company
    },
    canArchive(item) {
      return item.role === 'owner' || item.role === 'company_admin' || !!item.capabilities.archive_company
    },
    roleLabel(role) {
      const labels = {
        owner: 'Responsable',
        company_admin: 'Administrador',
        member: 'Miembro',
        external_collaborator: 'Colaborador externo',
        viewer: 'Visualizador',
      }
      return labels[role] || role || 'Sin rol'
    },
    notifyError(error) {
      const message = error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : 'No fue posible completar la operación.'
      this.$store.globalStore.dispatch('addMessage', {
        message,
        title: 'Error',
        created: new Date(),
        type: 'error',
        icon: 'mdi-alert-circle',
        color: '#F4516C'
      })
    },
  },
}
</script>

<style scoped>
/* ── Contenedor ───────────────────────────────────── */
.cl-container { padding: 20px; }

/* ── Loading ──────────────────────────────────────── */
.cl-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.cl-loading-text { font-size: 13px; color: #888; }

/* ── Búsqueda ─────────────────────────────────────── */
.cl-search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  background: #ffffff;
  border: 1px solid #D0D9EF;
  border-radius: 24px;
  padding: 6px 14px;
  max-width: 320px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.cl-search-box--active,
.cl-search-box:focus-within {
  border-color: #2D4B95;
  box-shadow: 0 0 0 3px rgba(45, 75, 149, 0.08);
}
.cl-search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 12.5px;
  color: #1C3A7A;
  width: 100%;
}
.cl-search-input::placeholder { color: #6b80ab; }

/* ── Tarjeta ──────────────────────────────────────── */
.cl-card {
  border-radius: 14px;
  border: 1px solid #E0E6F5;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s;
  animation: cl-fade-in 0.35s ease both;
}
.cl-card:hover {
  box-shadow: 0 6px 24px rgba(45, 75, 149, 0.12);
  transform: translateY(-2px);
}
.cl-card--selected {
  border-color: #2D4B95;
  box-shadow: 0 0 0 2px rgba(45, 75, 149, 0.15);
}

@keyframes cl-fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Franja superior ──────────────────────────────── */
.cl-card-top {
  background: linear-gradient(135deg, #1C3A7A 0%, #2D4B95 100%);
  height: 56px;
  position: relative;
  display: flex;
  justify-content: center;
}
.cl-status {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 10px !important;
  font-weight: 600;
  height: 18px !important;
  border: 1px solid transparent !important;
}
.cl-status--active {
  background: rgba(62, 191, 163, 0.18) !important;
  color: #b7f2e3 !important;
  border-color: rgba(62, 191, 163, 0.5) !important;
}
.cl-status--inactive {
  background: rgba(255, 255, 255, 0.12) !important;
  color: rgba(255, 255, 255, 0.7) !important;
  border-color: rgba(255, 255, 255, 0.25) !important;
}

/* ── Logo ─────────────────────────────────────────── */
.cl-logo-wrap {
  position: absolute;
  bottom: -24px;
}
.cl-logo {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 3px solid #ffffff;
  object-fit: cover;
  background: #ffffff;
  display: block;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
.cl-logo-fallback {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 3px solid #ffffff;
  background: #8097C4;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

/* ── Cuerpo ───────────────────────────────────────── */
.cl-card-body {
  padding: 34px 16px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}
.cl-name {
  font-size: 14px;
  font-weight: 600;
  color: #1C3A7A;
  text-align: center;
  margin-bottom: 4px;
  line-height: 1.3;
}
.cl-role {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  color: #2D4B95;
  background: #EEF2FB;
  border: 1px solid #C5D0EA;
  border-radius: 20px;
  padding: 2px 10px;
  text-align: center;
}

/* ── Info rows ────────────────────────────────────── */
.cl-info-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.cl-info-row {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
}
.cl-info-row--top { align-items: flex-start; }
.cl-info-row--top .v-icon { margin-top: 1px; }
.cl-info-text {
  font-size: 11px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cl-info-text--wrap {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
}

/* ── Acciones ─────────────────────────────────────── */
.cl-actions {
  width: 100%;
  margin-top: auto;
  padding-top: 12px;
}
.cl-actions-row {
  display: flex;
  gap: 6px;
  justify-content: center;
  border-top: 1px solid #F0F4FB;
  padding-top: 12px;
}
.cl-btn {
  font-size: 11px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 26px !important;
  padding: 0 10px !important;
  flex: 1;
}

/* ── Estado vacío ─────────────────────────────────── */
.cl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 40px 20px;
  border: 1.5px dashed #D0D9EF;
  border-radius: 14px;
  background: #f7f9fc;
  text-align: center;
}
.cl-empty-title { font-size: 14px; font-weight: 600; color: #1C3A7A; margin-top: 4px; }
.cl-empty-text { font-size: 12px; color: #8896b3; }

/* ── Modal edición ────────────────────────────────── */
.company-modal {
  border-radius: 16px !important;
  overflow: hidden;
  background: #ffffff;
  display: flex !important;
  flex-direction: column !important;
  max-height: 98vh;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px 16px;
  background: linear-gradient(135deg, #2D4B95 0%, #1a2f63 100%);
  flex-shrink: 0;
}
.header-left { display: flex; align-items: center; gap: 12px; }
.header-icon-wrap {
  width: 36px; height: 36px; border-radius: 10px;
  background: rgba(255,255,255,0.15);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.modal-title { font-size: 14px; font-weight: 600; color: #fff; line-height: 1.2; }
.modal-subtitle { font-size: 11px; color: rgba(255,255,255,0.6); margin-top: 2px; }
.close-btn { background: rgba(255,255,255,0.1) !important; border-radius: 8px !important; }

.modal-body {
  padding: 0;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.form-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.scroll-area {
  overflow-y: auto;
  padding: 20px 22px;
  flex: 1 1 auto;
  min-height: 0;
}

.field-grid {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 14px 16px;
}
.field-group { display: flex; flex-direction: column; }
.field-group.span-2 { grid-column: span 2; }
.field-label {
  font-size: 12px; font-weight: 600; color: #5a6a8a;
  margin-bottom: 6px;
}

/deep/ .custom-field .v-input__slot { border-radius: 8px !important; min-height: 40px; }
/deep/ .custom-field.v-text-field--outlined fieldset { border-color: #e2e8f4; border-width: 1.5px; }
/deep/ .custom-field.v-text-field--outlined.v-input--is-focused fieldset { border-color: #2D4B95 !important; }
/deep/ .custom-field input, /deep/ .custom-field textarea { font-size: 13px; color: #2d3748; }
/deep/ .custom-field .v-text-field__slot input::placeholder,
/deep/ .custom-field textarea::placeholder { color: #a4b0c9; }
/deep/ .custom-field.error--text fieldset { border-color: #F4516C !important; }

.modal-footer {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 8px; padding: 13px 22px;
  border-top: 1px solid #e8edf5; background: #f7f9fc;
}
.btn-cancel {
  padding: 8px 16px; border-radius: 8px;
  border: 1.5px solid #d1d9e8; background: transparent;
  font-size: 13px; font-weight: 500; color: #5a6a8a; cursor: pointer;
}
.btn-cancel:hover { background: #eef2fb; border-color: #b0bedd; }
.btn-save {
  display: flex; align-items: center;
  padding: 8px 18px; border-radius: 8px; border: none;
  background: linear-gradient(135deg, #2D4B95, #1a2f63);
  font-size: 13px; font-weight: 600; color: white;
  cursor: pointer; box-shadow: 0 3px 10px rgba(45,75,149,0.3);
}
.btn-save:hover:not(:disabled) { box-shadow: 0 5px 14px rgba(45,75,149,0.4); }
.btn-save:disabled { opacity: 0.6; cursor: not-allowed; box-shadow: none; }

@media (max-width: 600px) {
  .field-grid { grid-template-columns: 1fr; }
  .field-group.span-2 { grid-column: span 1; }
}
</style>

<style>
.edit-company-dialog.v-dialog {
  max-height: 98vh;
}
</style>